import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Company, Hold } from '../services/api';

interface SendMoneyState {
  // Modal visibility states
  isOpen: boolean;
  showProcessing: boolean;
  showComplete: boolean;
  
  // Form data
  currentStep: number;
  formData: {
    receiverCompanyId: string;
    amount: string;
  };
  
  // Transaction data
  transactionData: any;
  transData: {
    transactionId: string;
    duration: number;
  };
  
  // Hold data (for escrow flow)
  holdData: Hold | null;
  escrowStatus: string;
  escrowMessage: string;
  
  // Company data
  currentCompany?: Company;
  availableCompanies: Company[];
  
  // Loading state
  isLoading: boolean;
  slot: number;
}

interface SendMoneyActions {
  // Modal controls
  openModal: () => void;
  closeModal: () => void;
  showProcessingModal: () => void;
  showCompleteModal: (transactionData: any) => void;
  
  // Form controls
  setCurrentStep: (step: number) => void;
  setFormData: (data: { receiverCompanyId: string; amount: string }) => void;
  updateFormData: (updates: Partial<{ receiverCompanyId: string; amount: string }>) => void;
  
  // Company data
  setCurrentCompany: (company: Company | undefined) => void;
  setAvailableCompanies: (companies: Company[]) => void;
  
  // Loading state
  setLoading: (loading: boolean) => void;
  
  // Transaction completion
  handleProcessingComplete: (data: any) => void;
  handleNewSettlement: () => void;
  onSubmit: (data: { receiverCompanyId: string; amount: string }) => void;
  
  // Escrow flow
  setHoldData: (hold: Hold | null) => void;
  setEscrowStatus: (status: string, message: string) => void;
  clearEscrowData: () => void;
}

type SendMoneyContextType = SendMoneyState & SendMoneyActions;

const SendMoneyContext = createContext<SendMoneyContextType | undefined>(undefined);

interface SendMoneyProviderProps {
  children: ReactNode;
  onSubmit: (data: { receiverCompanyId: string; amount: string }) => void;
  isOpen?: boolean;
  onClose?: () => void;
  showProcessingModal?: boolean;
//   transactionId?: string;
//   duration?: number;
//   slot?: number;
}

export const SendMoneyProvider: React.FC<SendMoneyProviderProps> = ({ 
  children, 
  onSubmit,
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  // showProcessingModal: externalShowProcessing,
//   transactionId: externalTransactionId,
//   duration: externalDuration,
//   slot: externalSlot,
}) => {
  const [state, setState] = useState<SendMoneyState>({
    isOpen: externalIsOpen || false,
    showProcessing: false,
    showComplete: false,
    currentStep: 1,
    formData: {
      receiverCompanyId: '',
      amount: '',
    },
    transactionData: null,
    transData: {
      transactionId: '',
      duration: 3,
    },
    currentCompany: undefined,
    availableCompanies: [],
    isLoading: false,
    slot: 0,
    holdData: null,
    escrowStatus: '',
    escrowMessage: '',
  });

  // Handle external state changes
  React.useEffect(() => {
    if (externalIsOpen !== undefined) {
      setState(prev => ({ ...prev, isOpen: externalIsOpen }));
    }
  }, [externalIsOpen]);

//   React.useEffect(() => {
//     if (externalSlot !== undefined) {
//       setState(prev => ({ ...prev, slot: externalSlot }));
//     }
//   }, [externalSlot]);

//   React.useEffect(() => {
//     if (externalShowProcessing && externalTransactionId && externalDuration) {
//       setState(prev => ({
//         ...prev,
//         showProcessing: true,
//         transData: { transactionId: externalTransactionId, duration: externalDuration },
//       }));
//     }
//   }, [externalShowProcessing, externalTransactionId, externalDuration]);

  const actions: SendMoneyActions = {
    openModal: () => {
      setState(prev => ({
        ...prev,
        isOpen: true,
        showProcessing: false,
        showComplete: false,
        currentStep: 1,
        formData: { receiverCompanyId: '', amount: '' },
        transactionData: null,
        transData: { transactionId: '', duration: 3 },
      }));
    },

    closeModal: () => {
      setState(prev => ({
        ...prev,
        isOpen: false,
        showProcessing: false,
        showComplete: false,
        currentStep: 1,
        formData: { receiverCompanyId: '', amount: '' },
        transactionData: null,
        transData: { transactionId: '', duration: 3 },
      }));
      // Call external onClose if provided
      if (externalOnClose) {
        externalOnClose();
      }
    },

    showProcessingModal: () => {
      // console.log('showProcessingModal is called');
      setState(prev => ({
        ...prev,
        showProcessing: true,
        // transData: { transactionId, duration },
      }));
    },

    showCompleteModal: (transactionData: any) => {
      setState(prev => ({
        ...prev,
        showProcessing: false,
        showComplete: true,
        transactionData,
      }));
    },

    setCurrentStep: (step: number) => {
      setState(prev => ({ ...prev, currentStep: step }));
    },

    setFormData: (data: { receiverCompanyId: string; amount: string }) => {
      setState(prev => ({ ...prev, formData: data }));
    },

    updateFormData: (updates: Partial<{ receiverCompanyId: string; amount: string }>) => {
      setState(prev => ({
        ...prev,
        formData: { ...prev.formData, ...updates },
      }));
    },

    setCurrentCompany: (company: Company | undefined) => {
      setState(prev => ({ ...prev, currentCompany: company }));
    },

    setAvailableCompanies: (companies: Company[]) => {
      setState(prev => ({ ...prev, availableCompanies: companies }));
    },

    setLoading: (loading: boolean) => {
      setState(prev => ({ ...prev, isLoading: loading }));
    },

    handleProcessingComplete: (data: any) => {
      setState(prev => ({
        ...prev,
        showProcessing: false,
        showComplete: true,
        transactionData: data,
        slot: data.slot,
        currentStep: 1,
      }));
      // Call the original onSubmit to actually process the transaction
    //   onSubmit(state.formData);
    },

    handleNewSettlement: () => {
      setState(prev => ({
        ...prev,
        showComplete: false,
        transactionData: null,
        formData: { receiverCompanyId: '', amount: '' },
        currentStep: 1,
      }));
    },

    onSubmit: (data: { receiverCompanyId: string; amount: string }) => {
      onSubmit(data);
    },

    setHoldData: (hold: Hold | null) => {
      setState(prev => ({ ...prev, holdData: hold }));
    },

    setEscrowStatus: (status: string, message: string) => {
      setState(prev => ({ 
        ...prev, 
        escrowStatus: status, 
        escrowMessage: message 
      }));
    },

    clearEscrowData: () => {
      setState(prev => ({ 
        ...prev, 
        holdData: null, 
        escrowStatus: '', 
        escrowMessage: '' 
      }));
    },
  };

  const contextValue: SendMoneyContextType = {
    ...state,
    ...actions,
  };

  return (
    <SendMoneyContext.Provider value={contextValue}>
      {children}
    </SendMoneyContext.Provider>
  );
};

export const useSendMoney = (): SendMoneyContextType => {
  const context = useContext(SendMoneyContext);
  if (context === undefined) {
    throw new Error('useSendMoney must be used within a SendMoneyProvider');
  }
  return context;
};
